import React from 'react';
import { Container } from '@ui';
import { TextField } from '@ui/inputs';
import { SpecEditorContext } from './SpecEditorContext';
import { Consequence } from './styles';

// The escape hatch: raw JSON for ONE sub-object.
//
// Why scoped rather than one tree over the whole definition — the old `json-edit-react` surface
// took all four sub-objects at once, which had two consequences worth stating. A key typed at the
// root was injected into the definition and survived to the server, where lenient validation
// accepted it and the strict compile later rejected it as an unknown top-level key. And deleting
// one of the four sub-keys was impossible, because the change handler spread the picked sub-objects
// back over the previous definition. A pane that owns exactly one sub-object cannot do either.
//
// Cadence: it writes on EVERY keystroke that parses. There is therefore never a valid edit sitting
// uncommitted for a save to race, and text that does not parse is held and never written — a
// half-typed brace must not reach the buffer, and it must not freeze the rest of the page either.
//
// Two pieces of state design worth knowing:
//
//   * The held text lives in the editor's Context, not here, because this component unmounts
//     whenever the panel is collapsed, switched back to Form, or jumped away from by a [Go] in the
//     Problems strip. Local state would mean unparsed work vanishing on a collapse, silently.
//   * Re-seeding keys on `bufferEpoch`, not on a value comparison. A revert that restores the
//     section to the value it already had changes no value, so a comparison cannot see it — and
//     the pane would sit on stale unparsed text and its error forever.

const print = (value) => JSON.stringify(value === undefined ? {} : value, null, 2);

export const RawPane = ({ panel, sectionKey }) => {
  const { definition, replaceSection, rawText, setRawText, bufferEpoch } = React.useContext(SpecEditorContext);

  const committedText = print(definition ? definition[sectionKey] : undefined);
  const held = rawText[panel];

  // Held text is tagged with the epoch it was typed against. A newer epoch means the buffer was
  // replaced from outside the form, so whatever is held is stale.
  const isStale = !held || held.epoch !== bufferEpoch;
  const text = isStale ? committedText : held.text;
  const parseError = isStale ? null : held.error;

  const onChange = (nextText) => {
    let parsed;
    try {
      parsed = JSON.parse(nextText);
    } catch (e) {
      setRawText(panel, { text: nextText, error: e.message, epoch: bufferEpoch });
      return;
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setRawText(panel, { text: nextText, error: 'This section must be a JSON object.', epoch: bufferEpoch });
      return;
    }
    setRawText(panel, { text: nextText, error: null, epoch: bufferEpoch });
    replaceSection(sectionKey, parsed);
  };

  return (
    <div>
      <TextField
        multiline
        mono
        rows={14}
        spellCheck={false}
        label={`Raw ${sectionKey}`}
        value={text}
        onChange={onChange}
        invalid={!!parseError}
        hint={
          parseError
            ? `Not applied — ${parseError}. The last version that parsed is still what will be saved.`
            : 'Edits apply as you type, as soon as the text parses.'
        }
      />
      <Container top={6}>
        <Consequence>
          The same checks run over this pane as over the form, so the problem count above stays honest here. What the
          form prevents structurally, this pane does not — it accepts anything the editor has never heard of, which is
          the point of having it.
        </Consequence>
      </Container>
    </div>
  );
};
